import * as React from 'react';
import {
    Badge,
    Button,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    Field,
    Input,
    makeStyles,
    OverlayDrawer,
    Select,
    SpinButton,
    Spinner,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, Dismiss20Regular, Dismiss24Regular, Edit20Regular, Library24Regular } from '@fluentui/react-icons';
import {
    ICreatePollTemplateRequest,
    IPollTemplate,
    IUpdatePollTemplateRequest,
    PollQuestionType,
} from '../services/DataService';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useAppToast } from '../app/ToastContext';
import { useIsMobile } from '../app/useMediaQuery';
import { getQuestionTypeLabel, pluralize, QUESTION_TYPE_OPTIONS } from '../lib/pollLabels';
import { isChoiceType, isTextType } from '../lib/pollQuestions';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import ErrorBar from '../components/ui/ErrorBar';
import EmptyState from '../components/ui/EmptyState';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { TableSkeleton } from '../components/ui/Skeletons';
import { QUESTION_TYPE_ICONS } from '../components/forms/QuestionTypePicker';

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        columnGap: '14px',
        alignItems: 'center',
        padding: '12px 20px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        ':first-child': {
            borderTop: 'none',
        },
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '@media (max-width: 767px)': {
            padding: '12px 16px',
        },
    },
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
    text: {
        fontWeight: tokens.fontWeightSemibold,
        overflowWrap: 'anywhere',
    },
    meta: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px 10px',
        flexWrap: 'wrap',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    actions: {
        display: 'flex',
        gap: '2px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    choices: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    choiceRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '8px',
        alignItems: 'center',
    },
    drawer: {
        width: 'min(520px, 100vw)',
    },
});

interface IDraftChoice {
    id?: string;
    text: string;
    placeholder: string;
}

interface IDraft {
    id?: string;
    order: number;
    text: string;
    typeCode: PollQuestionType;
    choices: IDraftChoice[];
    placeholder: string;
}

const emptyChoices = (): IDraftChoice[] => [{ text: '', placeholder: '' }, { text: '', placeholder: '' }];

const TemplatesPage: React.FC = () => {
    const styles = useStyles();
    const toast = useAppToast();
    const isMobile = useIsMobile();
    const dataService = useDataService();
    const templates = useAsync(() => dataService.getTemplates(), []);

    const [draft, setDraft] = React.useState<IDraft>();
    const [showErrors, setShowErrors] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string>();
    const [deleting, setDeleting] = React.useState<IPollTemplate>();
    const [deleteBusy, setDeleteBusy] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string>();

    const sorted = React.useMemo(() => (templates.data ?? []).slice().sort((a, b) => a.Order - b.Order), [templates.data]);

    const openCreate = (): void => {
        const nextOrder = sorted.length ? Math.max(...sorted.map((t) => t.Order)) + 1 : 1;
        setShowErrors(false);
        setSaveError(undefined);
        setDraft({ order: nextOrder, text: '', typeCode: PollQuestionType.SingleChoice, choices: emptyChoices(), placeholder: '' });
    };

    const openEdit = (template: IPollTemplate): void => {
        const choices = (template.Choices ?? []).slice().sort((a, b) => a.Order - b.Order)
            .map((c) => ({ id: c.Id, text: c.Text, placeholder: c.Placeholder ?? '' }));
        setShowErrors(false);
        setSaveError(undefined);
        setDraft({
            id: template.Id,
            order: template.Order,
            text: template.Text,
            typeCode: template.TypeCode as PollQuestionType,
            choices: choices.length >= 2 ? choices : [...choices, ...emptyChoices()].slice(0, Math.max(2, choices.length)),
            placeholder: isTextType(template.TypeCode as PollQuestionType) ? (template.Choices?.[0]?.Placeholder ?? '') : '',
        });
    };

    const patch = (p: Partial<IDraft>): void => setDraft((prev) => (prev ? { ...prev, ...p } : prev));

    const draftChoice = !!draft && isChoiceType(draft.typeCode);
    const draftText = !!draft && isTextType(draft.typeCode);
    const textError = draft && !draft.text.trim() ? 'Укажите текст вопроса.' : undefined;
    const choicesError = draft && draftChoice && draft.choices.filter((c) => c.text.trim()).length < 2
        ? 'Нужно минимум два варианта.'
        : undefined;

    const save = async (): Promise<void> => {
        if (!draft) return;
        if (textError || choicesError) {
            setShowErrors(true);
            return;
        }
        const choices = draftChoice
            ? draft.choices.filter((c) => c.text.trim()).map((c, i) => ({ Id: c.id, Text: c.text.trim(), Order: i + 1, Placeholder: c.placeholder.trim() || undefined }))
            : draftText && draft.placeholder.trim()
                ? [{ Id: draft.choices[0]?.id, Text: draft.placeholder.trim(), Order: 1, Placeholder: draft.placeholder.trim() }]
                : [];
        try {
            setSaving(true);
            setSaveError(undefined);
            if (draft.id) {
                const request: IUpdatePollTemplateRequest = { Order: draft.order, Text: draft.text.trim(), TypeCode: draft.typeCode, Choices: choices };
                await dataService.updateTemplate(draft.id, request);
                toast.success('Шаблон обновлён');
            } else {
                const request: ICreatePollTemplateRequest = {
                    Order: draft.order,
                    Text: draft.text.trim(),
                    TypeCode: draft.typeCode,
                    Choices: choices.map(({ Text, Order, Placeholder }) => ({ Text, Order, Placeholder })),
                };
                await dataService.createTemplate(request);
                toast.success('Шаблон создан');
            }
            setDraft(undefined);
            templates.reload();
        } catch (e) {
            setSaveError((e as Error)?.message ?? 'Не удалось сохранить шаблон.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (): Promise<void> => {
        if (!deleting) return;
        try {
            setDeleteBusy(true);
            setDeleteError(undefined);
            await dataService.deleteTemplate(deleting.Id);
            toast.success('Шаблон удалён');
            setDeleting(undefined);
            templates.reload();
        } catch (e) {
            setDeleteError((e as Error)?.message ?? 'Не удалось удалить шаблон. Возможно, он используется в опросах.');
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                title="Шаблоны вопросов"
                subtitle="Типовые формулировки, которые можно подставить в любой опрос одним нажатием."
                actions={[
                    { key: 'create', label: 'Новый шаблон', icon: <Add20Regular />, appearance: 'primary', onClick: openCreate },
                ]}
            />

            <ErrorBar message={templates.error} title="Не удалось загрузить шаблоны" onRetry={templates.reload} />

            <Section flush>
                {templates.loading && <TableSkeleton rows={5} />}

                {!templates.loading && !templates.error && sorted.length === 0 && (
                    <div style={{ padding: 16 }}>
                        <EmptyState
                            icon={<Library24Regular />}
                            title="Шаблонов пока нет"
                            description="Сохраните часто задаваемые вопросы, и они появятся в конструкторе опроса в списке «Из шаблона»."
                            action={{ label: 'Новый шаблон', onClick: openCreate, icon: <Add20Regular /> }}
                        />
                    </div>
                )}

                {!templates.loading && sorted.length > 0 && (
                    <div className={styles.list} role="list">
                        {sorted.map((template) => {
                            const type = template.TypeCode as PollQuestionType;
                            const choices = (template.Choices ?? []).slice().sort((a, b) => a.Order - b.Order);
                            return (
                                <div key={template.Id} role="listitem" className={styles.row}>
                                    <span className={styles.icon} aria-hidden>{QUESTION_TYPE_ICONS[type] ?? QUESTION_TYPE_ICONS[PollQuestionType.Text]}</span>
                                    <div className={styles.body}>
                                        <Text className={styles.text}>{template.Text}</Text>
                                        <div className={styles.meta}>
                                            <Badge appearance="tint" color="brand" size="small" shape="rounded">{getQuestionTypeLabel(type)}</Badge>
                                            {isChoiceType(type) && choices.length > 0 && (
                                                <span title={choices.map((c) => c.Text).join(', ')}>
                                                    {pluralize(choices.length, ['вариант', 'варианта', 'вариантов'])}: {choices.map((c) => c.Text).join(' · ')}
                                                </span>
                                            )}
                                            <span>Порядок {template.Order}</span>
                                        </div>
                                    </div>
                                    <div className={styles.actions}>
                                        <Tooltip content="Редактировать" relationship="label" withArrow>
                                            <Button appearance="subtle" icon={<Edit20Regular />} onClick={() => openEdit(template)} />
                                        </Tooltip>
                                        <Tooltip content="Удалить" relationship="label" withArrow>
                                            <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => { setDeleteError(undefined); setDeleting(template); }} />
                                        </Tooltip>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Section>

            <OverlayDrawer
                position="end"
                size={isMobile ? 'full' : 'medium'}
                open={!!draft}
                onOpenChange={(_, data) => { if (!data.open && !saving) setDraft(undefined); }}
                className={styles.drawer}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={<Button appearance="subtle" aria-label="Закрыть" icon={<Dismiss24Regular />} disabled={saving} onClick={() => setDraft(undefined)} />}
                    >
                        {draft?.id ? 'Редактировать шаблон' : 'Новый шаблон'}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    {draft && (
                        <div className={styles.form}>
                            <ErrorBar message={saveError} onDismiss={() => setSaveError(undefined)} />

                            <Field label="Тип вопроса" required>
                                <Select
                                    value={String(draft.typeCode)}
                                    onChange={(_, data) => {
                                        const typeCode = Number(data.value) as PollQuestionType;
                                        patch({ typeCode, choices: isChoiceType(typeCode) && draft.choices.length < 2 ? emptyChoices() : draft.choices });
                                    }}
                                >
                                    {QUESTION_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={String(option.value)}>{option.label}</option>
                                    ))}
                                </Select>
                            </Field>

                            <Field label="Текст вопроса" required validationMessage={showErrors ? textError : undefined}>
                                <Input value={draft.text} autoFocus maxLength={500} onChange={(_, data) => patch({ text: data.value })} placeholder="Например: Оцените организацию мероприятия" />
                            </Field>

                            <Field label="Порядок в списке" hint="Шаблоны сортируются по этому числу.">
                                <SpinButton
                                    value={draft.order}
                                    min={1}
                                    onChange={(_, data) => {
                                        const value = data.value ?? Number(data.displayValue);
                                        if (value !== null && !Number.isNaN(value)) patch({ order: Math.max(1, Number(value)) });
                                    }}
                                />
                            </Field>

                            {draftChoice && (
                                <Field label="Варианты ответа" required validationMessage={showErrors ? choicesError : undefined}>
                                    <div className={styles.choices}>
                                        {draft.choices.map((choice, index) => (
                                            <div key={index} className={styles.choiceRow}>
                                                <Input
                                                    value={choice.text}
                                                    placeholder={`Вариант ${index + 1}`}
                                                    aria-label={`Вариант ${index + 1}`}
                                                    onChange={(_, data) => {
                                                        const choices = draft.choices.slice();
                                                        choices[index] = { ...choices[index], text: data.value };
                                                        patch({ choices });
                                                    }}
                                                />
                                                <Button
                                                    appearance="subtle"
                                                    icon={<Dismiss20Regular />}
                                                    aria-label={`Удалить вариант ${index + 1}`}
                                                    disabled={draft.choices.length <= 2}
                                                    onClick={() => patch({ choices: draft.choices.filter((_, i) => i !== index) })}
                                                />
                                            </div>
                                        ))}
                                        <div>
                                            <Button appearance="secondary" size="small" icon={<Add20Regular />} onClick={() => patch({ choices: [...draft.choices, { text: '', placeholder: '' }] })}>
                                                Добавить вариант
                                            </Button>
                                        </div>
                                    </div>
                                </Field>
                            )}

                            {draftText && (
                                <Field label="Подсказка в поле ответа" hint="Необязательно.">
                                    <Input value={draft.placeholder} onChange={(_, data) => patch({ placeholder: data.value })} placeholder="Например: Напишите пару слов" />
                                </Field>
                            )}
                        </div>
                    )}
                </DrawerBody>
                <DrawerFooter>
                    <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : undefined} disabled={saving} onClick={() => { void save(); }}>
                        {saving ? 'Сохраняем' : 'Сохранить'}
                    </Button>
                    <Button appearance="secondary" disabled={saving} onClick={() => setDraft(undefined)}>Отмена</Button>
                </DrawerFooter>
            </OverlayDrawer>

            <ConfirmDialog
                open={!!deleting}
                title="Удалить шаблон?"
                confirmLabel="Удалить"
                busyLabel="Удаление"
                busy={deleteBusy}
                error={deleteError}
                danger
                onCancel={() => { if (!deleteBusy) setDeleting(undefined); }}
                onConfirm={() => { void remove(); }}
            >
                <Text>Шаблон <strong>«{deleting?.Text}»</strong> исчезнет из списка. Уже созданные опросы не изменятся.</Text>
            </ConfirmDialog>
        </>
    );
};

export default TemplatesPage;
